import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Profile = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  school: string | null;
  bio: string | null;
  date_of_birth: string | null;
  phone_number: string | null;
};

type AuthContextType = {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, firstName: string, lastName: string, dateOfBirth: string, school?: string | null, phoneNumber?: string | null) => Promise<{user: User} | null>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  uploadAvatar: (file: File) => Promise<string | null>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("[AuthProvider] Initialization started");
    
    // We need to ensure the auth state doesn't flicker on page load
    // by using a consistent loading state during initialization
    setLoading(true);
    
    let mounted = true;
    
    // Set up auth state listener FIRST before checking existing session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, currentSession) => {
        console.log("[AuthProvider] Auth state changed:", _event, currentSession?.user?.email);
        
        if (!mounted) return;
        
        if (currentSession?.user) {
          setSession(currentSession);
          setUser(currentSession.user);
          
          // Check for new device login on SIGNED_IN event
          if (_event === 'SIGNED_IN') {
            // Defer the new device check to avoid deadlocks
            setTimeout(() => {
              if (mounted) {
                checkNewDeviceLogin(currentSession.user.id);
              }
            }, 0);
          }
          
          // Don't call fetchProfile directly in the callback to avoid deadlocks
          // Use setTimeout to defer the call until after the callback completes
          setTimeout(() => {
            if (mounted) {
              fetchProfile(currentSession.user.id);
            }
          }, 0);
        } else {
          setSession(null);
          setUser(null);
          setProfile(null);
        }
      }
    );

    // AFTER setting up the listener, check for existing session
    const initializeAuth = async () => {
      try {
        console.log("[AuthProvider] Checking for existing session...");
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        if (currentSession?.user) {
          console.log("[AuthProvider] Found existing session for user:", currentSession.user.email);
          setSession(currentSession);
          setUser(currentSession.user);
          await fetchProfile(currentSession.user.id);
        } else {
          console.log("[AuthProvider] No existing session found");
          setSession(null);
          setUser(null);
          setProfile(null);
        }
      } catch (error) {
        console.error("[AuthProvider] Error checking auth session:", error);
      } finally {
        if (mounted) {
          console.log("[AuthProvider] Authentication loading complete");
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      console.log("[AuthProvider] Fetching profile for user:", userId);
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("[AuthProvider] Error fetching profile:", error);
        return;
      }

      console.log("[AuthProvider] Profile fetched successfully:", data);
      setProfile(data);
    } catch (error) {
      console.error("[AuthProvider] Error in fetchProfile:", error);
    }
  };

  const checkNewDeviceLogin = async (userId: string) => {
    try {
      // Get current device fingerprint
      const userAgent = navigator.userAgent;
      const deviceKey = btoa(userAgent).substring(0, 32);
      const storedDeviceKey = localStorage.getItem('anyskill_device_key');
      
      if (storedDeviceKey && storedDeviceKey !== deviceKey) {
        // New device detected - send email notification
        console.log("[AuthProvider] New device login detected");
        
        try {
          await supabase.functions.invoke('send-notification-email', {
            body: {
              user_id: userId,
              email_type: 'new_device_login',
              subject: '🔐 New device login detected on your AnySkill account',
              content: {
                title: 'New Device Login Alert',
                message: 'A login to your AnySkill account was detected from a new device or browser. If this was you, no action is needed.',
                device_info: userAgent,
                login_time: new Date().toLocaleString()
              }
            }
          });
          console.log("[AuthProvider] New device login email sent");
        } catch (emailError) {
          console.error("[AuthProvider] Failed to send new device email:", emailError);
        }
      }
      
      // Store current device key
      localStorage.setItem('anyskill_device_key', deviceKey);
    } catch (error) {
      console.error("[AuthProvider] Error checking new device:", error);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const cleanEmail = email.trim().toLowerCase();
      let { error } = await supabase.auth.signInWithPassword({ email: cleanEmail, password });
      // Password managers/autofill sometimes add stray whitespace — retry once trimmed
      if (error && /invalid login credentials/i.test(error.message) && password !== password.trim()) {
        ({ error } = await supabase.auth.signInWithPassword({ email: cleanEmail, password: password.trim() }));
      }
      if (error) throw error;
    } catch (error: any) {
      throw error;
    }
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string, dateOfBirth: string, school?: string | null, phoneNumber?: string | null) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      const { error, data } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName,
            last_name: lastName,
            date_of_birth: dateOfBirth,
            school: school || null,
            phone_number: phoneNumber || null,
          },
        },
      });
      
      if (error) throw error;

      if (data.user) {
        // Ensure profile has DOB, school, and phone stored as well
        try {
          await supabase.from('profiles').update({ 
            date_of_birth: dateOfBirth,
            school: school || null,
            phone_number: phoneNumber || null
          }).eq('id', data.user.id);
        } catch {}
        toast.success("Account created successfully!");
        return data;
      } else {
        toast.info("Account created, but unable to log in automatically");
        return null;
      }
    } catch (error: any) {
      toast.error(error.message || "Error signing up");
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      // Even if there's an error (e.g., session already expired server-side),
      // we should still clear local state so the user can sign out
      if (error) {
        console.warn("[AuthProvider] Sign out warning:", error.message);
      }
      // Clear local state regardless of server response
      setSession(null);
      setUser(null);
      setProfile(null);
      toast.info("Signed out successfully");
    } catch (error: any) {
      // Still clear local state on any error - user needs to be able to sign out
      setSession(null);
      setUser(null);
      setProfile(null);
      console.error("[AuthProvider] Sign out error:", error);
      toast.info("Signed out");
    }
  };

  const uploadAvatar = async (file: File): Promise<string | null> => {
    try {
      if (!user) throw new Error("User not authenticated");
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      const filePath = fileName;

      // Upload the file to Supabase storage
      const { error: uploadError } = await supabase
        .storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data } = supabase
        .storage
        .from('avatars')
        .getPublicUrl(filePath);

      const avatar_url = data.publicUrl;

      // Update the user profile with the avatar URL
      await updateProfile({ avatar_url });
      
      return avatar_url;
    } catch (error: any) {
      toast.error(error.message || "Error uploading avatar");
      console.error("Error uploading avatar:", error);
      return null;
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    try {
      if (!user) throw new Error("User not authenticated");
      
      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", user.id);
      
      if (error) throw error;
      
      setProfile(prev => prev ? { ...prev, ...updates } : null);
      toast.success("Profile updated successfully");
    } catch (error: any) {
      toast.error(error.message || "Error updating profile");
      throw error;
    }
  };

  const value = {
    session,
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    uploadAvatar,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
